import React from "react";
import { toTitleCase } from "../../../utils/stringUtils";
import NoDataState from "../../../components/ui/NoDataState";
import Dialog from "../../../components/ui/Dialog";
import { appraisalAdminService } from "../../../services/appraisalAdminService";
import { authService } from "../../../services";

type TimelineEvent = {
  id: string;
  title: string;
  when: string;
  status?: string;
  note?: string;
  actionBy?: string;
  relationshipType?: string;
};

type EmployeeInfo = {
  employeeId: string;
  name: string;
  manager?: string;
  department?: string;
  designation?: string;
  managerRating?: string;
  feedbackRating?: string;
  overallRating?: string;
  employeeManager?: string;
  hodRating?: string;
  hrRating?: string;
  adminStatus?: string; // 'pending' | 'submitted'
};

interface Props {
  timeline?: TimelineEvent[];
  employee?: EmployeeInfo;
}

const EmployeeDetailsTimeline: React.FC<Props> = ({ timeline, employee }) => {
  const [hrRating, setHrRating] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [showResultDialog, setShowResultDialog] = React.useState(false);
  const [resultMessage, setResultMessage] = React.useState({ title: "", message: "", type: "success" as "success" | "error" });
  
  const effectiveTimeline = timeline ?? [];
  const defaultEmployee: EmployeeInfo = {
    employeeId: "000000",
    name: "Jane Doe",
    manager: "John Doe (000218)",
    department: "Digital Solutions",
    designation: "Assistant Manager",
    managerRating: "4/5",
    feedbackRating: "2.4/4",
    employeeManager: "John Doe (000218)",
  };
  const effectiveEmployee = employee ?? defaultEmployee;

  // Check if admin status is submitted (disable editing)
  const isSubmitted = effectiveEmployee.adminStatus === 'submitted';

  // Rating labels mapping
  const ratingLabels: Record<number, string> = {
    1: "1-Excel",
    2: "2-Fully Met",
    3: "3-Mostly Met",
    4: "4-Requires Action",
  };

  // Helper function to get rating label
  const getRatingLabel = (rating: string): string => {
    let numericPart = rating.split('/')[0];
    
    // If it contains a label already (like "1-Excel"), extract just the number
    if (numericPart.includes('-')) {
      numericPart = numericPart.split('-')[0];
    }
    
    const numValue = parseFloat(numericPart);
    if (isNaN(numValue)) return "";
    const roundedValue = Math.round(numValue);
    return ratingLabels[roundedValue] || "";
  };

  // Helper function to format rating with label (e.g., "4-Requires Action / 4")
  const formatRatingWithLabel = (rating: string | undefined): string => {
    if (!rating) return "";
    
    const label = getRatingLabel(rating);
    if (label) {
      return `${label}`;
    }
    return rating;
  };

  // Helper function to format rating (keep the value/4 format)
  const formatRating = (rating: string | undefined): string => {
    if (!rating) return "";
    // Keep the full format like "3.8/4" or "4/4"
    return rating;
  };

  // Initialize HR rating from employee data
  React.useEffect(() => {
    if (effectiveEmployee.hrRating) {
      setHrRating(formatRating(effectiveEmployee.hrRating));
    }
  }, [effectiveEmployee.hrRating]);

  const handleRatingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    // If empty, clear the rating
    if (value === '') {
      setHrRating('');
    } else {
      // Store with /4 suffix
      setHrRating(`${value}/4`);
    }
  };

  const handleSaveClick = () => {
    // Validate rating - extract numeric value from "X/4" format
    const numericValue = hrRating.split('/')[0];
    const ratingValue = parseFloat(numericValue);
    if (isNaN(ratingValue)) {
      setResultMessage({
        title: "Invalid Rating",
        message: "Please enter a valid rating value.",
        type: "error"
      });
      setShowResultDialog(true);
      return;
    }
    
    if (ratingValue < 0 || ratingValue > 4) {
      setResultMessage({
        title: "Invalid Rating",
        message: "Rating must be between 0 and 4.",
        type: "error"
      });
      setShowResultDialog(true);
      return;
    }
    
    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirmDialog(false);
    setIsSaving(true);
    
    try {
      const submitterEmail = authService.getStoredUser()?.email;
      if (!submitterEmail) {
        throw new Error("User email not found");
      }
      
      // Extract numeric value from "X/4" format
      const numericValue = hrRating.split('/')[0];
      const ratingValue = parseFloat(numericValue);
      const response = await appraisalAdminService.updateHrRating(
        submitterEmail,
        effectiveEmployee.employeeId,
        ratingValue
      );
      
      setResultMessage({
        title: "Success",
        message: response.message || "HR rating has been successfully updated.",
        type: "success"
      });
      setShowResultDialog(true);
      
      // Reload page after showing success message (with a small delay)
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      console.error("Error saving HR rating:", error);
      const errorMessage = error?.message || error?.response?.data?.message || "Failed to save HR rating. Please try again.";
      setResultMessage({
        title: "Error",
        message: errorMessage,
        type: "error"
      });
      setShowResultDialog(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to get status styling
  const statusStyles: Record<string, { bgColor: string; textColor: string; dotColor: string }> = {
    completed: {
      bgColor: "bg-green-100",
      textColor: "text-green-700",
      dotColor: "bg-green-600",
    },
    approved: {
      bgColor: "bg-green-100",
      textColor: "text-green-700",
      dotColor: "bg-green-600",
    },
    submitted: {
      bgColor: "bg-blue-100",
      textColor: "text-blue-700",
      dotColor: "bg-blue-600",
    },
    "in progress": {
      bgColor: "bg-blue-100",
      textColor: "text-blue-700",
      dotColor: "bg-blue-600",
    },
    in_progress: {
      bgColor: "bg-blue-100",
      textColor: "text-blue-700",
      dotColor: "bg-blue-600",
    },
    "in-progress": {
      bgColor: "bg-blue-100",
      textColor: "text-blue-700",
      dotColor: "bg-blue-600",
    },
    pending: {
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-700",
      dotColor: "bg-yellow-400",
    },
    rejected: {
      bgColor: "bg-red-100",
      textColor: "text-red-700",
      dotColor: "bg-red-500",
    },
  };

  const getStatusStyling = (status?: string) => {
    const key = status?.trim().toLowerCase();
    if (key && statusStyles[key]) {
      return statusStyles[key];
    }

    return {
      bgColor: "bg-gray-100",
      textColor: "text-gray-700",
      dotColor: "bg-gray-400",
    };
  };

  const formatStatusLabel = (status?: string) => {
    if (!status) {
      return "Status Unavailable";
    }

    return toTitleCase(
      status
        .replace(/[_-]+/g, " ")
        .toLowerCase()
    );
  };

  const formatRelationshipType = (relationshipType?: string) => {
    if (!relationshipType) {
      return null;
    }

    if (relationshipType.trim().toLowerCase().replace(/[\s-]+/g, "_") === "dotted_superior") {
      return "Additional Superior";
    }

    return toTitleCase(relationshipType.replace(/[_-]+/g, " "));
  };

  // Helper function to format date/time
  const formatDateTime = (dateString: string) => {
    if (!dateString || dateString === "Date not available") {
      return "Date not available";
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if not a valid date
      }

      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateString; // Return original on any error
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-full min-h-0">
      {/* Nomination Timeline */}
      {/* <div className="flex-1 min-w-0">
        <div className="mb-4">
          <div className="flex  bg-purple-50 text-purple-700 px-5 py-3 rounded-lg text-base font-medium">
            Nomination Timeline
          </div>
        </div>

        <div className="space-y-8 px-5 max-h-[70vh] overflow-y-auto pr-2 lg:max-h-[75vh]">
          {effectiveTimeline.length === 0 ? (
            <NoDataState
              title="No Timeline Data"
              description="Timeline updates will appear here once available."
              className="bg-gray-50"
            />
          ) : (
            effectiveTimeline.map((ev, index) => {
              const styling = getStatusStyling(ev.status);
              const isLastItem = index === effectiveTimeline.length - 1;

              return (
                <div key={ev.id} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-4 h-4 rounded-full ${styling.dotColor} mt-1 shadow`}
                    />
                    {!isLastItem && (
                      <div
                        className="w-px bg-gray-200 flex-1 mt-2"
                        style={{ minHeight: 56 }}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center justify-start gap-2 sm:gap-4">
                      <h4 className="text-sm font-medium text-gray-800">
                        {ev.title}
                      </h4>
                      <span
                        className={`text-xs font-medium ${styling.bgColor} ${styling.textColor} px-3 py-1 rounded-full`}
                      >
                        {formatStatusLabel(ev.status)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                      {formatDateTime(ev.when)}
                    </div>
                    {(ev.actionBy || ev.relationshipType) && (
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        {ev.actionBy && (
                          <div>
                            <span className="font-medium text-gray-700">Action By:</span>{" "}
                            {ev.actionBy}
                          </div>
                        )}
                        {ev.relationshipType && (
                          <div>
                            <span className="font-medium text-gray-700">Relationship:</span>{" "}
                            {formatRelationshipType(ev.relationshipType)}
                          </div>
                        )}
                      </div>
                    )}
                    {ev.note && (
                      <div className="mt-2 text-sm text-gray-700 bg-gray-50 p-3 rounded-lg border-l-4 border-gray-300">
                        <span className="font-medium text-gray-800">Remarks:</span>{" "}
                        {ev.note}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div> */}

      {/* OKR Timeline */}
      {/* <div className="flex-1 min-w-0">
        <div className="mb-4">
          <div className="flex bg-purple-50 text-purple-700 px-5 py-3 rounded-lg text-base font-medium">
            OKR Timeline
          </div>
        </div>

        <div className="space-y-8 px-5">
          {effectiveTimeline.length === 0 ? (
            <NoDataState
              title="No Timeline Data"
              description="Timeline updates will appear here once available."
              className="bg-gray-50"
            />
          ) : (
            effectiveTimeline.map((ev, index) => {
              const styling = getStatusStyling(ev.status);
              const isLastItem = index === effectiveTimeline.length - 1;

              return (
                <div key={ev.id} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-4 h-4 rounded-full ${styling.dotColor} mt-1 shadow`}
                    />
                    {!isLastItem && (
                      <div
                        className="w-px bg-gray-200 flex-1 mt-2"
                        style={{ minHeight: 56 }}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center justify-start gap-2 sm:gap-4">
                      <h4 className="text-sm font-medium text-gray-800">
                        {ev.title}
                      </h4>
                      <span
                        className={`text-xs font-medium ${styling.bgColor} ${styling.textColor} px-3 py-1 rounded-full`}
                      >
                        {formatStatusLabel(ev.status)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                      {formatDateTime(ev.when)}
                    </div>
                    {(ev.actionBy || ev.relationshipType) && (
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        {ev.actionBy && (
                          <div>
                            <span className="font-medium text-gray-700">Action By:</span>{" "}
                            {ev.actionBy}
                          </div>
                        )}
                        {ev.relationshipType && (
                          <div>
                            <span className="font-medium text-gray-700">Relationship:</span>{" "}
                            {formatRelationshipType(ev.relationshipType)}
                          </div>
                        )}
                      </div>
                    )}
                    {ev.note && (
                      <div className="mt-2 text-sm text-gray-700 bg-gray-50 p-3 rounded-lg border-l-4 border-gray-300">
                        <span className="font-medium text-gray-800">Remarks:</span>{" "}
                        {ev.note}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div> */}

      {/* Appraisal Timeline */}
       <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <div className="mb-4">
          <div className="flex bg-purple-50 text-purple-700 px-5 py-3 rounded-lg text-base font-medium">
            Appraisal Timeline
          </div>
        </div>

        <div className="space-y-8 px-5 flex-1 overflow-y-auto pr-2">
          {effectiveTimeline.length === 0 ? (
            <NoDataState
              title="No Timeline Data"
              description="Timeline updates will appear here once available."
              className="bg-gray-50"
            />
          ) : (
            effectiveTimeline.map((ev, index) => {
              const styling = getStatusStyling(ev.status);
              const isLastItem = index === effectiveTimeline.length - 1;

              return (
                <div key={ev.id} className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-4 h-4 rounded-full ${styling.dotColor} mt-1 shadow`}
                    />
                    {!isLastItem && (
                      <div
                        className="w-px bg-gray-200 flex-1 mt-2"
                        style={{ minHeight: 56 }}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center justify-start gap-2 sm:gap-4">
                      <h4 className="text-sm font-medium text-gray-800">
                        {ev.title}
                      </h4>
                      <span
                        className={`text-xs font-medium ${styling.bgColor} ${styling.textColor} px-3 py-1 rounded-full`}
                      >
                        {formatStatusLabel(ev.status)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                      {formatDateTime(ev.when)}
                    </div>
                    {(ev.actionBy || ev.relationshipType) && (
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        {ev.actionBy && (
                          <div>
                            <span className="font-medium text-gray-700">Action By:</span>{" "}
                            {ev.actionBy}
                          </div>
                        )}
                        {ev.relationshipType && (
                          <div>
                            <span className="font-medium text-gray-700">Relationship:</span>{" "}
                            {formatRelationshipType(ev.relationshipType)}
                          </div>
                        )}
                      </div>
                    )}
                    {ev.note && (
                      <div className="mt-2 text-sm text-gray-700 bg-gray-50 p-3 rounded-lg border-l-4 border-gray-300">
                        <span className="font-medium text-gray-800">Remarks:</span>{" "}
                        {ev.note}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Employee Information */}
      <div className="flex-2 min-w-0 lg:max-w-md lg:h-full lg:overflow-y-auto lg:pr-2">
        <div className="mb-4">
          <div className="flex bg-purple-50 text-purple-700 px-5 py-3 rounded-lg text-base font-medium">
            Employee Information
          </div>
        </div>
        <div className="px-5 text-sm text-gray-700 space-y-4">
          <div className="flex justify-between gap-4">
            <span className="text-gray-500 whitespace-nowrap">Employee ID</span>
            <span className="font-semibold text-sm text-right break-words">
              {effectiveEmployee.employeeId?.trim() || '-'}
            </span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500 whitespace-nowrap">Name of Employee</span>
            <span className="font-semibold text-sm text-right break-words">
              {effectiveEmployee.name?.trim() ? toTitleCase(effectiveEmployee.name) : '-'}
            </span>
          </div>

            <div className="flex justify-between gap-4">
            <span className="text-gray-500 whitespace-nowrap">Reporting Manager</span>
            <span className="font-semibold text-sm text-right break-words">
              {effectiveEmployee.employeeManager?.trim() || '-'}
            </span>
          </div>


              <div className="flex justify-between gap-4">
            <span className="text-gray-500 whitespace-nowrap">Department</span>
            <span className="font-semibold text-sm text-right break-words">
              {effectiveEmployee.department?.trim() || '-'}
            </span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500 whitespace-nowrap">Designation</span>
            <span className="font-semibold text-sm text-right break-words">
              {effectiveEmployee.designation?.trim() || '-'}
            </span>
          </div>


    <div className="flex justify-between gap-4">
            <span className="text-gray-500 whitespace-nowrap">Gentari Way 360 Feedback Rating</span>
            <span className="font-semibold text-sm text-right">
              {effectiveEmployee.feedbackRating && effectiveEmployee.feedbackRating !== "N/A" 
                ? formatRatingWithLabel(effectiveEmployee.feedbackRating)
                : effectiveEmployee.feedbackRating}
            </span>
          </div>


  <div className="flex justify-between gap-4">
            <span className="text-gray-500 whitespace-nowrap">OKR Rating</span>
            <span className="font-semibold text-sm text-right">
              {effectiveEmployee.managerRating && effectiveEmployee.managerRating !== "N/A" 
                ? formatRatingWithLabel(effectiveEmployee.managerRating)
                : effectiveEmployee.managerRating}
            </span>
          </div>


    <div className="flex justify-between gap-4">
            <span className="text-gray-500 whitespace-nowrap">OKR Rating (HOD)</span>
            <span className="font-semibold text-sm text-right">


                 {effectiveEmployee.hodRating && effectiveEmployee.hodRating !== "N/A" 
                ? formatRatingWithLabel(effectiveEmployee.hodRating)
                : effectiveEmployee.hodRating}
            </span>
          </div>


          <div className="flex justify-between items-center gap-4">
            <span className="text-gray-500 whitespace-nowrap">OKR Rating (HR)</span>
            <div className="flex items-center gap-2">
              <select
                value={(() => {
                  let numericPart = hrRating.split('/')[0];
                  // If it contains a label (like "1-Excel"), extract just the number
                  if (numericPart.includes('-')) {
                    numericPart = numericPart.split('-')[0];
                  }
                  return numericPart;
                })()}
                onChange={handleRatingChange}
                disabled={isSubmitted}
                className="px-3 py-1.5 text-sm font-semibold border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-700 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                style={{ minWidth: "150px" }}
              >
                <option value="">Select Rating</option>
                <option value="1">1-Excel</option>
                <option value="2">2-Fully Met</option>
                <option value="3">3-Mostly Met</option>
                <option value="4">4-Requires Action</option>
              </select>
              <button
                onClick={handleSaveClick}
                disabled={isSaving || isSubmitted}
                className="px-4 py-1.5 text-sm font-medium text-purple-700 bg-purple-100 border border-purple-300 rounded hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? "SAVING..." : "SAVE"}
              </button>
            </div>
          </div>


      
        
      
          <div className="flex justify-between gap-4">
            <span className="text-gray-500 whitespace-nowrap">Overall Rating</span>
            <span className="font-bold text-sm text-right">
              {effectiveEmployee.overallRating && effectiveEmployee.overallRating !== "N/A" 
                ? formatRatingWithLabel(effectiveEmployee.overallRating)
                : effectiveEmployee.overallRating}
            </span>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="Submit"
        message="Please note that the changes made by you will affect the overall rating of the employee. Are you sure you want to continue?"
        note="Once submitted you will not be able to make changes to the employee's rating anymore."
        type="warning"
        confirmText="Submit"
        cancelText="Cancel"
        showCancel={true}
        onConfirm={handleConfirmSave}
      />

      {/* Result Dialog */}
      <Dialog
        isOpen={showResultDialog}
        onClose={() => setShowResultDialog(false)}
        title={resultMessage.title}
        message={resultMessage.message}
        type={resultMessage.type}
        confirmText="OK"
        onConfirm={() => setShowResultDialog(false)}
      />
    </div>
  );
};

export default EmployeeDetailsTimeline;
